import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Zap, Rocket, Star, Shield, Bell } from 'lucide-react';

const iconMap = {
  sparkles: Sparkles,
  zap: Zap,
  rocket: Rocket,
  star: Star,
  shield: Shield,
  bell: Bell
};

const FeatureAnnouncementModal = ({ announcement, isOpen, onClose, onDismiss }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen && announcement) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, announcement]);

  if (!announcement) return null;

  const IconComponent = iconMap[announcement.featureIcon?.toLowerCase()] || Sparkles;
  const stats = announcement.stats || { seenPercentage: 87 };

  const handleGotIt = () => {
    onDismiss(announcement.id, dontShowAgain);
    onClose();
  };

  // Helper to format title without duplication
  const getFormattedTitle = (title) => {
    let base = title;
    const prefix = "Introducing ";
    const suffix = " – Your New Superpower!";
    
    if (base.startsWith(prefix)) base = base.substring(prefix.length);
    if (base.endsWith(suffix)) base = base.substring(0, base.length - suffix.length);
    if (base.endsWith(" – Your New Superpower!")) base = base.substring(0, base.length - " – Your New Superpower!".length);
    
    return `${prefix}${base}${suffix}`;
  };

  return (
    <Transition.Root show={isVisible} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl transition-all my-4 sm:my-8 w-full max-w-[95%] sm:max-w-2xl border border-white/20">
                {/* Global Close Button - Top Right of Modal */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 z-[110] p-2 text-white/80 hover:text-white md:text-gray-400 md:hover:text-gray-600 bg-black/20 md:bg-transparent rounded-full backdrop-blur-md md:backdrop-blur-none transition-all shadow-lg md:shadow-none"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>

                <div className="flex flex-col md:flex-row h-full">
                  {/* Left Side: Illustration / Icon (Gradient Area) */}
                  <div className="md:w-2/5 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 p-6 sm:p-8 flex flex-col items-center justify-center text-white relative overflow-hidden min-h-[200px] md:min-h-full">
                    {/* Glassmorphism accent shapes */}
                    <div className="absolute top-[-10%] -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute bottom-[-10%] -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                    
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 20 }}
                      className="relative z-10"
                    >
                      {announcement.featureIllustration ? (
                        <img 
                          src={announcement.featureIllustration} 
                          alt="Feature Illustration" 
                          className="w-full h-auto max-w-[140px] sm:max-w-[180px] drop-shadow-2xl rounded-2xl"
                        />
                      ) : (
                        <div className="w-20 h-24 sm:w-24 sm:h-24 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center border border-white/30 shadow-xl">
                          <IconComponent className="w-10 h-10 sm:w-12 sm:h-12 text-white" strokeWidth={1.5} />
                        </div>
                      )}
                    </motion.div>

                    <div className="mt-4 sm:mt-8 text-center relative z-10">
                      <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider mb-2">
                        New Feature
                      </div>
                      <h4 className="text-lg sm:text-xl font-bold leading-tight">Empower Your Workflow</h4>
                    </div>

                    {/* Progress Indicator - hidden on mobile if too small, shown on md+ */}
                    <div className="hidden md:block absolute bottom-6 left-0 right-0 px-8">
                      <div className="flex items-center justify-between text-[10px] font-medium text-white/70 mb-1.5">
                        <span>New users adoption</span>
                        <span>{stats.seenPercentage}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${stats.seenPercentage}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                          className="h-full bg-white"
                        />
                      </div>
                      <p className="text-[9px] text-white/50 mt-2 text-center">
                        Join {stats.seenCount || 'many'} others who've discovered this!
                      </p>
                    </div>
                  </div>

                  {/* Right Side: Content Area */}
                  <div className="md:w-3/5 p-6 sm:p-8 bg-white relative">
                    <div className="pr-0 md:pr-4">
                      <Dialog.Title as="h3" className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-tight">
                        {getFormattedTitle(announcement.title)}
                      </Dialog.Title>
                      
                      <div className="mt-3 sm:mt-4 prose prose-sm text-slate-600 leading-relaxed text-sm">
                        {announcement.content && (
                          <div dangerouslySetInnerHTML={{ __html: announcement.content }} />
                        )}
                      </div>

                      {/* Benefits List */}
                      {announcement.benefits && announcement.benefits.length > 0 && (
                        <ul className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
                          {announcement.benefits.map((benefit, idx) => (
                            <motion.li 
                              key={idx}
                              initial={{ x: 20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.4 + (idx * 0.1) }}
                              className="flex items-start gap-3"
                            >
                              <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center border border-indigo-100">
                                <CheckCircleIcon className="w-3.5 h-3.5 text-indigo-600" />
                              </div>
                              <span className="text-xs sm:text-sm font-medium text-slate-700">{benefit}</span>
                            </motion.li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Adoption indicator for mobile */}
                    <div className="md:hidden mt-6 pt-4 border-t border-slate-100">
                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">
                        <span>User Adoption</span>
                        <span>{stats.seenPercentage}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-indigo-500 transition-all duration-1000"
                          style={{ width: `${stats.seenPercentage}%` }}
                        />
                      </div>
                    </div>

                    <div className="mt-8 sm:mt-10">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleGotIt}
                        className="w-full py-3 sm:py-4 px-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-sm sm:text-base shadow-lg shadow-indigo-200 hover:shadow-indigo-300 transition-all flex items-center justify-center gap-2"
                      >
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5 fill-white" />
                        ✅ Got it!
                      </motion.button>

                      <div className="mt-4 flex items-center justify-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <div className="relative flex items-center">
                            <input
                              type="checkbox"
                              checked={dontShowAgain}
                              onChange={(e) => setDontShowAgain(e.target.checked)}
                              className="peer sr-only"
                            />
                            <div className="w-4 h-4 border-2 border-slate-300 rounded peer-checked:border-indigo-600 peer-checked:bg-indigo-600 transition-all" />
                            <CheckCircleIcon className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity left-0.5" />
                          </div>
                          <span className="text-[10px] sm:text-xs font-semibold text-slate-500 group-hover:text-slate-700 transition-colors">
                            Don't show this again
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default FeatureAnnouncementModal;

